import {
  PingMessage,
  SocketChannelName,
  SocketMessage,
  SocketMessageType,
  SocketSubchannelName,
  SubscriptionMessage
} from '@cex/shared-modules';
import env from '@beam-australia/react-env';

const PING_INTERVAL_DELAY = 10000;

class SocketClient {
  socket?: WebSocket;
  pingInterval?: NodeJS.Timeout;
  channel?;

  private updatePing(isOn = false) {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = undefined;
    }

    if (isOn) {
      this.pingInterval = setInterval(() => {
        if (this.isOpen()) {
          const pingMessage: PingMessage = { type: SocketMessageType.PING };
          this.sendMessage(pingMessage);
        }
      }, PING_INTERVAL_DELAY);
    }
  }

  addEventListener(message, listener) {
    this.socket?.addEventListener(message, listener);
  }

  open(token: string) {
    const url = `${env('SOCKET_ROOT')}/consumer/ws?access_token=${token}`;

    if (this.isOpen()) {
      this.close();
    }

    if (this.socket) {
      return;
    }

    this.socket = new WebSocket(url);

    this.socket.onclose = () => {
      this.socket?.send(
        JSON.stringify({
          type: SocketMessageType.STATE,
          chan_name: SocketChannelName.STATE_CLOSED
        })
      );
      this.socket?.close();
    };

    this.socket.onerror = (event) => {
      // TODO: Add error handling via toast
      console.error('Socket error: ', event);
      this.socket?.send(
        JSON.stringify({
          type: SocketMessageType.STATE,
          chan_name: SocketChannelName.ERROR
        })
      );
      this.socket?.close();
    };

    this.socket.onopen = () => {
      this.updatePing(true);
      this.socket?.send(
        JSON.stringify({
          type: SocketMessageType.STATE,
          chan_name: SocketChannelName.STATE_OPEN
        })
      );
    };

    return () => {
      this.updatePing();
    };
  }

  close() {
    this.socket?.close();
    this.channel = undefined;
    this.socket = undefined;
  }

  subscribe(channelName: SocketChannelName, subchannelName?: SocketSubchannelName) {
    const message: SubscriptionMessage = {
      type: SocketMessageType.SUBSCRIBE,
      chan_name: channelName
    };
    if (subchannelName) {
      message.subchan_name = subchannelName;
    }
    this.sendMessage(message);
  }

  unsubscribe(channelName: SocketChannelName, subchannelName?: SocketSubchannelName) {
    const message: SubscriptionMessage = {
      type: SocketMessageType.UNSUBSCRIBE,
      chan_name: channelName
    };
    if (subchannelName) {
      message.subchan_name = subchannelName;
    }
    this.sendMessage(message);
  }

  sendMessage(message: SocketMessage | string) {
    const data = JSON.stringify(message);
    this.socket?.send(data);
  }

  sendData(data: string) {
    this.socket?.send(data);
  }

  isOpen() {
    if (!this.socket) return false;
    return (this.socket && this.socket.readyState === WebSocket.OPEN) || false;
  }

  isClosed() {
    if (!this.socket) return true;

    return (this.socket && this.socket.readyState > this.socket.OPEN) || false;
  }
}

export const socket = new SocketClient();

export const waitForOpenConnection = () => {
  return new Promise<void>((resolve, reject) => {
    const maxNumberOfAttempts = 10;
    const intervalTime = 200; //ms

    let currentAttempt = 0;
    const interval = setInterval(() => {
      if (currentAttempt > maxNumberOfAttempts - 1) {
        clearInterval(interval);
        reject(new Error('Maximum number of attempts exceeded'));
      } else if (socket.isOpen()) {
        clearInterval(interval);
        resolve();
      }
      currentAttempt++;
    }, intervalTime);
  });
};
